/**
 * Hard-coded book of business for the prototype — there is no backend, so this is the whole
 * universe of accounts, strategies, funds and opening holdings.
 *
 * Shapes follow the production concepts: an ACCOUNT owns many STRATEGIES, each strategy carries its
 * own `activationCode` (the code that goes into a transaction ID), and holdings are tracked per
 * strategy per fund in UNITS.
 */

/**
 * The two client books.
 *
 * They share one `accounts` array — and therefore one unit ledger, one reconciliation queue and one
 * registry — because the fund side of the business does not care which desk raised a transaction.
 * The segment only decides which desk's workspace an account appears in.
 */
export const SEGMENTS = { UNIT_TRUST: 'UNIT_TRUST', WEALTH_MANAGEMENT: 'WEALTH_MANAGEMENT' };

export const SEGMENT_LABELS = {
    [SEGMENTS.UNIT_TRUST]: 'Unit Trust',
    [SEGMENTS.WEALTH_MANAGEMENT]: 'Wealth Management'
};

/** Fund master data. Codes and names are the real ones from the fund dashboard catalogue. */
export const FUNDS = [
    { fundId: 'UTLMF', fundName: 'LYNEAR Wealth Liquid Money Fund', navPrice: 12.4517, shortName: 'Liquid' },
    { fundId: 'UTIF', fundName: 'LYNEAR Wealth Income Fund', navPrice: 24.8903, shortName: 'Income' },
    { fundId: 'UTDOF', fundName: 'LYNEAR Wealth Dynamic Opportunities Fund', navPrice: 18.2264, shortName: 'Dynamic' }
];

export const FUND_BY_ID = FUNDS.reduce((acc, f) => ({ ...acc, [f.fundId]: f }), {});

/**
 * Accounts with multiple strategies each. `holdings` is units held per fund at the strategy level;
 * a fund missing from the map simply means zero units held.
 */
export const ACCOUNTS = [
    {
        accountId: 'ACC-000101',
        accountName: 'Dilshan Wijesinghe',
        accountType: 'individual',
        strategies: [
            {
                strategyId: 'STR-000201',
                strategyName: 'Growth Portfolio',
                activationCode: 'IN000271',
                holdings: { UTLMF: 12500.0, UTIF: 8400.5, UTDOF: 3200.0 }
            },
            {
                strategyId: 'STR-000202',
                strategyName: 'Retirement Plan',
                activationCode: 'IN000272',
                holdings: { UTLMF: 5000.0, UTIF: 15750.25 }
            }
        ]
    },
    {
        accountId: 'ACC-000102',
        accountName: 'Hasini De Silva',
        accountType: 'individual',
        strategies: [
            {
                strategyId: 'STR-000203',
                strategyName: 'Conservative Income',
                activationCode: 'IN000273',
                holdings: { UTIF: 22000.0, UTLMF: 1800.75 }
            },
            {
                strategyId: 'STR-000204',
                strategyName: 'Education Fund',
                activationCode: 'IN000274',
                holdings: { UTDOF: 9600.0 }
            },
            {
                strategyId: 'STR-000205',
                strategyName: 'Short Term Parking',
                activationCode: 'IN000275',
                holdings: { UTLMF: 40000.0 }
            }
        ]
    },
    {
        accountId: 'ACC-000103',
        accountName: 'Meridian Holdings (Pvt) Ltd',
        accountType: 'corporate',
        strategies: [
            {
                strategyId: 'STR-000206',
                strategyName: 'Treasury Operations',
                activationCode: 'IN000276',
                holdings: { UTLMF: 185000.0, UTIF: 62000.0 }
            },
            {
                strategyId: 'STR-000207',
                strategyName: 'Strategic Reserve',
                activationCode: 'IN000277',
                holdings: { UTIF: 48000.0, UTDOF: 27500.5 }
            }
        ]
    },
    {
        accountId: 'ACC-000104',
        accountName: 'Aravinda Perera',
        accountType: 'individual',
        strategies: [
            {
                strategyId: 'STR-000208',
                strategyName: 'Balanced Growth',
                activationCode: 'IN000278',
                holdings: { UTLMF: 7250.0, UTIF: 11300.0, UTDOF: 4150.25 }
            }
        ]
    },
    {
        accountId: 'ACC-000105',
        accountName: 'Nimali Fernando',
        accountType: 'individual',
        strategies: [
            {
                strategyId: 'STR-000209',
                strategyName: 'Wealth Builder',
                activationCode: 'IN000279',
                holdings: { UTIF: 33400.0 }
            },
            {
                strategyId: 'STR-000210',
                strategyName: 'Liquidity Pool',
                activationCode: 'IN000280',
                holdings: { UTLMF: 21750.5 }
            }
        ]
    },
    {
        accountId: 'ACC-000106',
        accountName: 'Cinnamon Ventures (Pvt) Ltd',
        accountType: 'corporate',
        strategies: [
            {
                strategyId: 'STR-000211',
                strategyName: 'Working Capital',
                activationCode: 'IN000281',
                holdings: { UTLMF: 95000.0, UTIF: 18200.0 }
            }
        ]
    },
    {
        accountId: 'ACC-000107',
        accountName: 'Kasun Jayawardena',
        accountType: 'individual',
        strategies: [
            {
                strategyId: 'STR-000212',
                strategyName: 'Long Term Growth',
                activationCode: 'IN000282',
                holdings: { UTDOF: 14600.0, UTIF: 6800.0 }
            }
        ]
    },
    {
        accountId: 'ACC-000108',
        accountName: 'Thilini Rajapaksa',
        accountType: 'individual',
        strategies: [
            {
                strategyId: 'STR-000213',
                strategyName: 'Retirement Income',
                activationCode: 'IN000283',
                holdings: { UTIF: 27900.0, UTLMF: 3400.0 }
            }
        ]
    },

    /*
     * Wealth management desk. Accounts without a `segment` default to the unit trust book (see
     * `seedAccounts` in the slice), so only these two need to say which desk they are on — and the
     * desk starts with a book rather than an empty screen.
     */
    {
        accountId: 'ACC-000109',
        accountName: 'Rohan Abeywardena',
        accountType: 'individual',
        segment: SEGMENTS.WEALTH_MANAGEMENT,
        strategies: [
            {
                strategyId: 'STR-000214',
                strategyName: 'General Investment',
                activationCode: 'IN000284',
                holdings: { UTIF: 41200.0, UTDOF: 18650.75 }
            },
            {
                strategyId: 'STR-000215',
                strategyName: 'Legacy Planning',
                activationCode: 'IN000285',
                holdings: { UTDOF: 22400.0 }
            }
        ]
    },
    {
        accountId: 'ACC-000110',
        accountName: 'Serendib Family Office (Pvt) Ltd',
        accountType: 'corporate',
        segment: SEGMENTS.WEALTH_MANAGEMENT,
        strategies: [
            {
                strategyId: 'STR-000216',
                strategyName: 'General Investment',
                activationCode: 'IN000286',
                holdings: { UTLMF: 128000.0, UTIF: 76500.0 }
            },
            {
                strategyId: 'STR-000217',
                strategyName: 'Opportunistic',
                activationCode: 'IN000287',
                holdings: { UTDOF: 31200.5 }
            }
        ]
    }
];

export const TRANSACTION_KINDS = {
    SUBSCRIPTION: 'SUBSCRIPTION',
    REDEMPTION: 'REDEMPTION',
    SWITCH: 'SWITCH',
    TRANSFER: 'TRANSFER'
};

export const TRANSACTION_KIND_LABELS = {
    [TRANSACTION_KINDS.SUBSCRIPTION]: 'Subscription',
    [TRANSACTION_KINDS.REDEMPTION]: 'Redemption',
    [TRANSACTION_KINDS.SWITCH]: 'Switching',
    [TRANSACTION_KINDS.TRANSFER]: 'Fund Transfer'
};
